import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GridRowModes, DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import * as services from "./../../services/saleng";
import AppBreadcrumbs from "./../../components/AppBreadcrumbs";
import toast, { Toaster } from "react-hot-toast";
import GroupFormDialog from "./GroupFormDialog";
import SearchIcon from "@mui/icons-material/Search";

const Products = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);

  const [searchText, setSearchText] = useState("");

  const filtered = rows.filter(
    (p) =>
      p.name.toLowerCase().includes(searchText.toLowerCase()) ||
      p.detail.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    { field: "no", headerName: "ลำดับ", flex: 0, width: 70 },
    { field: "name", headerName: "ชื่อกลุ่ม", flex: 0 },
    { field: "detail", headerName: "รายละเอียด", flex: 1 },
    {
      field: "actions",
      headerName: "",
      flex: 0,
      headerAlign: "center",
      align: "center",
      renderCell: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`${id}-save`}
              icon={<SaveIcon />}
              label="Save"
              color="primary"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={`${id}-cancel`}
              icon={<CancelIcon />}
              label="Cancel"
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={`${id}-edit`}
            icon={<EditNoteIcon />}
            label="Edit"
            color="primary"
            onClick={handleEditClick(id)}
          />,
          // <GridActionsCellItem
          //   key={`${id}-delete`}
          //   icon={<DeleteIcon />}
          //   label="Delete"
          //   color="error"
          //   onClick={handleDeleteClick(id)}
          // />,
        ];
      },
    },
    {
      field: "pricing",
      headerName: "",
      width: 150,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <Button
            variant="outlined"
            onClick={() =>
              navigate(
                `/groups/pricing/${params.row.id}?name=${params.row.name}&detail=${params.row.detail}`
              )
            }
          >
            จัดการราคา
          </Button>
        );
      },
    },
  ];

  const handleEditClick = (id) => () => {
    const editRow = rows.find((row) => row.id === id);
    setEditData(editRow);
    setOpenDialog(true);
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id) => () => {
    const deletedRow = rows.find((row) => row.id === id);

    console.log("deletedRow", deletedRow);
    Swal.fire({
      customClass: { container: "my-swal" },
      title: "ยืนยันที่จะลบ?",
      text: "คุณจะไม่สามารถย้อนกลับสิ่งนี้ได้!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) {
        services
          .deleteProduct(deletedRow.id)
          .then((response) => {
            toast.success("ลบสำเร็จ!");
            Swal.fire({
              customClass: { container: "my-swal" },
              title: "ลบแล้ว!",
              text: `${deletedRow.title} ถูกลบแล้ว`,
              icon: "success",
              confirmButtonText: "ตกลง",
            });
            setRows((prevRows) =>
              prevRows.filter((row) => row.id !== deletedRow.id)
            );
          })
          .catch((error) => {
            toast.error("ลบไม่สำเร็จ!");
            console.error("Error deleting product:", error);
          });
      }
    });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow) => {
    const price = Number(newRow.price);
    if (price < 0) newRow.price = 0;
    const payload = { ...newRow, price: newRow.price };

    services
      .updateProduct(payload, newRow.id)
      .then((response) => {
        toast.success("แก้ไขสำเร็จ!");
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
      })
      .catch((error) => {
        toast.error("แก้ไขไม่สำเร็จ!");
        console.error("Error creating product:", error);
      });

    return rows.find((row) => row.id === newRow.id);
  };

  const fetchData = useCallback(async () => {
    const res = await services.getGroups();
    const groups = res.data.data;
    console.log("groups ", groups);
    const groupsWithIndex = groups.map((item, index) => ({
      ...item,
      no: index + 1,
    }));
    setRows(groupsWithIndex);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <AppBreadcrumbs />
      <Toaster position="bottom-left" reverseOrder={false} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          my: 2,
        }}
      >
        {/* ส่วนซ้าย: หัวข้อ + Search */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography component="h2" variant="h5" sx={{ whiteSpace: "nowrap" }}>
            รายการกลุ่มลูกค้า
          </Typography>
          <TextField
            fullWidth
            label="ค้นหา"
            placeholder="ชื่อกลุ่ม / รายละเอียด"
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ maxWidth: { xs: "100%", sm: 420 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ปุ่มด้านขวา */}
        <Button
          color="success"
          variant="contained"
          onClick={() => {
            setEditData(null);
            setOpenDialog(true);
          }}
        >
          เพิ่มกลุ่มลูกค้า
        </Button>
      </Box>

      <Box sx={{ flex: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <Paper>
            <DataGrid
              rows={filtered.map((p, idx) => ({
                id: p.id,
                no: idx + 1,
                ...p,
              }))}
              columns={columns}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={setRowModesModel}
              processRowUpdate={processRowUpdate}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
              }}
            />
          </Paper>
        </Box>
      </Box>

      <GroupFormDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={() => {
          setLoading(true);
          fetchData();
        }}
        initialData={editData}
      />
    </>
  );
};

export default Products;
